import dayjs from 'dayjs';
import {useCallback, useMemo} from 'react';

import {DatePeriodEnum, getAnalyticsRangeByPeriodAndDate} from '../../../util/date';
import {useFormat} from '../../../util/format-hook/format-hook';
import {useUrl} from '../../../util/url-hook/url-hook';

import {REVIEWS_ANALYTICS_DELAY_DAYS} from './pages/keywords-and-topics-page/keywords-and-topics-form/keywords-and-topics-const';
import {
    getCompareModeState,
    getFilterStateFromUrlQuery,
    normalizeReviewAnalysisDateRange,
    serializeCompetitorDrawerFilters,
    serializePeriodDrawerFilters,
    serializeSingleDrawerFilters,
} from './reviews-analysis-helper';
import {
    ComparePeriodFilterRequestType,
    CompetitorFilterRequestType,
    ReviewsAnalysisFilterType,
    ReviewsCompareEnum,
    SingleFilterRequestType,
    UseReviewsAnalysisFilterType,
} from './reviews-analysis-type';

export function useReviewsAnalysisFilter(
    isTopicsAllowed = true,
    defaultDateRange?: [Date, Date]
): UseReviewsAnalysisFilterType {
    const {queries, replaceQuery} = useUrl<ReviewsAnalysisFilterType>();
    const filter: ReviewsAnalysisFilterType = useMemo<ReviewsAnalysisFilterType>(
        (): ReviewsAnalysisFilterType => getFilterStateFromUrlQuery(queries),
        [queries]
    );
    const compareMode: ReviewsCompareEnum = useMemo<ReviewsCompareEnum>((): ReviewsCompareEnum => {
        return getCompareModeState(filter);
    }, [filter]);
    const singleFilter: SingleFilterRequestType = useMemo<SingleFilterRequestType>(
        (): SingleFilterRequestType => serializeSingleDrawerFilters(filter, isTopicsAllowed, defaultDateRange),
        [defaultDateRange, filter, isTopicsAllowed]
    );
    const comparePeriodFilter: ComparePeriodFilterRequestType = useMemo<ComparePeriodFilterRequestType>(
        (): ComparePeriodFilterRequestType => serializePeriodDrawerFilters(filter),
        [filter]
    );
    const competitorFilter: CompetitorFilterRequestType = useMemo<CompetitorFilterRequestType>(
        (): CompetitorFilterRequestType => serializeCompetitorDrawerFilters(filter, defaultDateRange),
        [defaultDateRange, filter]
    );
    const setFilter = useCallback(
        (filterPartial: Partial<ReviewsAnalysisFilterType>): void => {
            replaceQuery(filterPartial);
        },
        [replaceQuery]
    );

    return {filter, singleFilter, comparePeriodFilter, competitorFilter, setFilter, queries, compareMode};
}

// Standard reviews-analysis fallback period (last month, offset by the analytics delay)
// for pages opened outside the filter drawer (e.g. the single-location / single-aspect
// pages). Memoized so the reference is stable — an unstable default range would make the
// filter recompute every render and cascade into refetch loops. Supplying it keeps
// date-scoped endpoints such as the AI summary from receiving empty dates.
export function useReviewsAnalysisDefaultDateRange(): [Date, Date] {
    return useMemo(() => {
        const {start, end} = getAnalyticsRangeByPeriodAndDate(
            dayjs().subtract(REVIEWS_ANALYTICS_DELAY_DAYS, 'day').toDate(),
            DatePeriodEnum.Month
        );

        return normalizeReviewAnalysisDateRange(start, end);
    }, []);
}

export function useReviewsAnalysisTooltipFormatter(): (label: string, compareLabelFormat?: boolean) => string {
    const {comparePeriodFilter, singleFilter} = useReviewsAnalysisFilter(false);
    const {getFormattedDateTime} = useFormat();

    const isCompareMode: boolean = useMemo(() => {
        return Boolean(comparePeriodFilter.startDate);
    }, [comparePeriodFilter.startDate]);

    return useCallback(
        (label: string, compareLabelFormat?: boolean) => {
            const startDate = singleFilter.startDate;
            const endDate = singleFilter.endDate;

            const compareStartDate = comparePeriodFilter.startDate;
            const compareEndDate = comparePeriodFilter.endDate;

            if (compareLabelFormat && compareStartDate && compareEndDate) {
                return `${getFormattedDateTime(new Date(compareStartDate), {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                })} - ${getFormattedDateTime(new Date(compareEndDate), {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                })}`;
            }

            if (isCompareMode && startDate && endDate) {
                return `${getFormattedDateTime(new Date(startDate), {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                })} - ${getFormattedDateTime(new Date(endDate), {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                })}`;
            }

            return label;
        },
        [comparePeriodFilter, getFormattedDateTime, isCompareMode, singleFilter]
    );
}
